import apiClient from './apiClient';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Visit Action-Pending API — Phase 6.0 overlay endpoints
 *
 *   POST  /api/organisation/visits/:id/action-pending
 *   POST  /api/organisation/visits/:id/action-pending/resolve
 *   GET   /api/organisation/visits/:id/action-pending/history
 *   POST  /api/organisation/visits/:id/dismiss-followup
 *
 * The overlay is a sparse marker ("this visit is currently with member
 * X for action Y"). Setting/resolving doesn't change the visit's
 * primary doctor (except the deliberate Receptionist→Doctor swap on
 * RESOLVED with a different target).
 * ═══════════════════════════════════════════════════════════════════════
 */

const BASE = '/api/organisation/visits';

/**
 * Set the action-pending overlay on a visit.
 * Direction matrix is enforced server-side (422 on bad hop).
 *
 * @param {string} visitId
 * @param {object} payload
 *   to_member_id, to_role, intent, comment?,
 *   attach_procedure_plan_id?, collect_bundled_payment?, bundled_payment?
 */
export const setActionPending = async (visitId, payload) => {
  const { data } = await apiClient.post(
    `${BASE}/${visitId}/action-pending`, payload,
  );
  return data;
};

/**
 * Refer a visit to another doctor (Phase 3.1).
 * Server decides bypass-vs-park based on the four A2 conditions.
 *
 * @param {string} visitId
 * @param {object} payload
 *   target_doctor_id (required), referral_reason?, payment_due? (rupees, float),
 *   receptionist_member_id? (required if bypass conditions don't all hold)
 * @returns {{ visit, history_entry, bypassed, bypass_reason }}
 */
export const referVisit = async (visitId, payload) => {
  const { data } = await apiClient.post(
    `${BASE}/${visitId}/refer`, payload,
  );
  return data;
};

/**
 * Resolve / forward / cancel the open overlay.
 *
 * @param {string} visitId
 * @param {object} payload
 *   resolution: 'RESOLVED' | 'FORWARD' | 'CANCEL',
 *   comment?, forward_to_member_id?, forward_to_role?, forward_intent?,
 *   attach_procedure_plan_id?, collect_bundled_payment?, bundled_payment?
 */
export const resolveActionPending = async (visitId, payload) => {
  const { data } = await apiClient.post(
    `${BASE}/${visitId}/action-pending/resolve`, payload,
  );
  return data;
};

/** GET full chronological history for a visit. */
export const getActionPendingHistory = async (visitId, { limit = 100 } = {}) => {
  const { data } = await apiClient.get(
    `${BASE}/${visitId}/action-pending/history`,
    { params: { limit } },
  );
  return data;  // { entries: [...], count }
};

/** Explicit dismiss of the doctor My-Tasks follow-up flag. */
export const dismissFollowup = async (visitId) => {
  const { data } = await apiClient.post(`${BASE}/${visitId}/dismiss-followup`);
  return data;  // { cleared: bool }
};

/** Action Required tray feed (receptionist / nurse). */
export const listMyActionPending = async ({ limit = 50 } = {}) => {
  const { data } = await apiClient.get(`${BASE}/me/action-pending`, {
    params: { limit },
  });
  return data;  // { visits: [...], count }
};

/** My Tasks lane feed (doctor). */
export const listMyFollowup = async ({ limit = 50 } = {}) => {
  const { data } = await apiClient.get(`${BASE}/me/followup`, {
    params: { limit },
  });
  return data;  // { visits: [...], count }
};

/**
 * Phase 6.0 / 8 — bundled payment.
 *
 * @param {string} visitId
 * @param {object} payload
 *   consultation_fee?, registration_fee?, payment_mode?,
 *   sitting_payments?: [{ plan_id, sitting_id, amount }, ...]
 */
export const collectBundledPayment = async (visitId, payload) => {
  const { data } = await apiClient.post(
    `${BASE}/${visitId}/collect-bundled-payment`, payload,
  );
  return data;  // { visit, components, total, errors }
};

const visitActionPendingApi = {
  setActionPending,
  resolveActionPending,
  getActionPendingHistory,
  dismissFollowup,
  listMyActionPending,
  listMyFollowup,
};

export default visitActionPendingApi;
